<template>
    <div :id="rootElementId" :style="`height:${SVG_RENDER_HEIGHT}px`"></div>
</template>

<script>
import { getDataUrl, getNeutralColors } from '../../utils/functions';
import { loadFonts, copyAndAppendElement } from '../../utils/exportToPNG';
import ResizeObserver from 'resize-observer-polyfill';
import UserService from '../../services/user.service';
import * as d3v4 from "d3v4";
import Dictionary from '../../dictionary';

  export default {
    name: 'XTChart',
    props: {
      id:{
        type: String,
        required: true
      },
      size: {
        type: Number,
        required: false,
      },
      margin: {
        type: Object,
        required: false,
      },
      type : {
        type: String,
        required: true,
      },
      plays: {
        type: Array,
        required: true,
      },
      typeOfPlay: {
        type: Array,
        required: true,
      },
      minXT: {
        type: Number,
        required: true,
      },
      maxXT: {
        type: Number,
        required: true,
      },
      playerName: {
        type: String,
        required: false
      },
      seasons: {
        type: Array,
        required: false
      },
      game: {
        type: Object,
        required: false
      },
      player: {
        type: Object,
        required: false
      },
      circlesAndBoxes: {
        type: Array,
        required: false
      },
      language: {
        type: String,
        required: false
      },
      homeFillColor:{
        type: String,
        default: "#ffffff"
      },
      awayFillColor:{
        type: String,
        default: "#ffffff"
      },
      mediaMode: {
        type: Boolean,
        default: false
      },
      homeBorderColor:{
        type: String,
        default: "#000000"
      },
      awayBorderColor:{
        type: String,
        default: "#000000"
      },
      homeTeamName:{
        type: String,
        default: ""
      },
      awayTeamName:{
        type: String,
        default: ""
      },
      //gameId for print one game Player xG Map
      gameId:{
        type:Number,
        default: 0
      },
      gameIds: {
        type: Array,
        required: false,
        default: () => []
      },
      gameName:{
        type: String,
        default: ""
      },
      fontFamily:{
        type: String,
        default: 'NeueMontreal'
      },
      minsPlayed: {
        type: Number,
        default: 0,
      },
      teamId: {
        type: Number,
        default: 0
      },
      filterShotTypeText: {
        type: String,
        default: ''
      },
      filterShotStrategyText: {
        type: String,
        default: ''
      },
      filterBodyPartText: {
        type: String,
        default: ''
      },
      filterMinutesText: {
        type: String,
        default: ''
      },
      filterTimeText: {
        type: String,
        default: ''
      },
      filterMinutesRange: {
        type: String,
        default: ''
      },
      filterActionEndingText: {
        type: String,
        default: ''
      },
      orientation: {
        type: String,
        default: 'horizontal'
      }
    },
    data() {
      return {
        height: 0,
        width: 0,
        images: {}
      }
    },
    computed: {
      SVG_WIDTH(){
        return this.size ? this.size : 380;
      },
      PITCH_WIDTH(){
        if(this.margin){
          return this.SVG_WIDTH - this.margin.left - this.margin.right
        }else{
          return this.SVG_WIDTH;
        }
      },
      PITCH_HEIGHT() {
        return this.game ? this.PITCH_WIDTH * 0.67125 : this.PITCH_WIDTH * 0.755
      },
      isVertical() {
        return this.orientation === 'vertical';
      },
      isMobile() {
        return this.$vuetify.breakpoint.smAndDown || this.$vuetify.breakpoint.mobile;
      },
      SVG_RENDER_WIDTH() {
        return this.isVertical ? this.PITCH_HEIGHT : this.SVG_WIDTH;
      },
      SVG_RENDER_HEIGHT() {
        return this.isVertical ? this.SVG_WIDTH : this.PITCH_HEIGHT;
      },
      rootElementId(){
        return `pitchXT${this.id}`
      },
      currentUserFieldColor() {
        return this.$store.getters["auth/fieldColor"];
      },
      currentUserLanguage() {
        return this.$store.getters["auth/language"];
      }
    },
    mounted() {
      this.observer = new ResizeObserver(this.onResize).observe(document.getElementById(this.rootElementId));
      this.printD3();
    },
    methods: {
      async printD3(force = false) {
        if (force) {
          this.images = {};
        }

        const SVG_WIDTH = this.SVG_WIDTH;
      const margin = this.margin ? this.margin : {top: 0, right: 0, bottom: 0, left: 0};
      const zeroMargin = {top: 0, right: 0, bottom: 0, left: 0};

      const PITCH_WIDTH = this.PITCH_WIDTH;
      const PITCH_HEIGHT = this.PITCH_HEIGHT;
      const renderW = this.isVertical ? PITCH_HEIGHT : PITCH_WIDTH;
      const renderH = this.isVertical ? PITCH_WIDTH : PITCH_HEIGHT;

      d3.selectAll('.tooltip').remove();

        d3.select(`#${this.rootElementId}`).select("svg").remove();

      const tooltip = d3
        .select("body")
        .append("div")
        .attr("class", "tooltip");

      var svg = d3.select(`#${this.rootElementId}`)
        .append("svg")
        .attr("width", this.isVertical ? PITCH_HEIGHT : SVG_WIDTH)
        .attr("height", this.isVertical ? SVG_WIDTH : PITCH_HEIGHT)
        .style("background-color", "#ffffff")
        .attr("class", "pitch")
        .attr("id", `${this.rootElementId}SVG`);

      const pitchLayer = svg.append("g").attr("transform",
        this.isVertical
          ? `translate(${margin.left},${margin.top + PITCH_WIDTH}) rotate(-90)`
          : `translate(${margin.left},${margin.top})`
      );
      const uiLayer = svg.append("g").attr("transform", `translate(${margin.left},${margin.top})`);

      this.playsFiltered = this.plays.filter(s => s.xT >= this.minXT && s.xT <= this.maxXT)

      this.drawImageBase64(pitchLayer, 0, 0, PITCH_WIDTH, PITCH_HEIGHT, require(`@/assets/img/${this.type === "game" ? "": "half_"}pitch_${this.currentUserFieldColor}.png`),'pitch',true);

      let neutralColorsHome = getNeutralColors('home');
      let firstHomeTeamColor = this.homeFillColor;
      let secondHomeTeamColor = this.type === "game" ? neutralColorsHome[1]  : "#000000";
      let neutralColorsAway = getNeutralColors('away');
      let firstAwayTeamColor = this.awayFillColor;
      let secondAwayTeamColor = this.type === "game" ? neutralColorsAway[1] : "#000000";
      if (this.type === "game" && !this.mediaMode) {
        firstHomeTeamColor = this.game.homeTeamGameColor ? this.game.homeTeamGameColor : (this.game.homeTeamColor ? this.game.homeTeamColor : firstHomeTeamColor);
        secondHomeTeamColor = this.game.homeTeamGameSecondColor ? this.game.homeTeamGameSecondColor : (this.game.homeTeamSecondColor ? this.game.homeTeamSecondColor : secondHomeTeamColor);
        firstAwayTeamColor = this.game.awayTeamGameColor ? this.game.awayTeamGameColor : (this.game.awayTeamColor ? this.game.awayTeamColor : firstAwayTeamColor);
        secondAwayTeamColor = this.game.awayTeamGameSecondColor ? this.game.awayTeamGameSecondColor : (this.game.awayTeamSecondColor ? this.game.awayTeamSecondColor : secondAwayTeamColor);
      }
      if(this.type === "game" && firstHomeTeamColor === firstAwayTeamColor){
        firstAwayTeamColor = neutralColorsAway[0];
      }
      if(this.type === "game" && secondHomeTeamColor === secondAwayTeamColor){
        secondAwayTeamColor = neutralColorsAway[1];
      }

      let logo  = require(`@/assets/img/driblab${this.mediaMode ? "_normal" : ""}_${this.currentUserFieldColor === 'white' ? 'color' : 'white'}.png`);

        if (this.type === "player") {
          const logoW = renderW * 0.17;
          const logoH = renderH * 0.09;
          this.drawImageBase64(uiLayer, renderW * 0.91 - logoW, renderH * 0.02 - 6, logoW, logoH, logo, "logoDriblab");
          this.printArrowTypeLegend(uiLayer, renderW, renderH, zeroMargin);

        if (this.circlesAndBoxes && this.circlesAndBoxes.includes("boxes")) {
            this.renderBoxes(pitchLayer, PITCH_WIDTH, PITCH_HEIGHT, zeroMargin, this.plays, this.language);
            if (this.isVertical) {
              pitchLayer.selectAll("text").each(function() {
                const el = d3.select(this);
                const x = +el.attr("x") || 0;
                const y = +el.attr("y") || 0;
                el.attr("transform", `rotate(90, ${x}, ${y})`);
              });
            }
        }
        if (this.circlesAndBoxes && this.circlesAndBoxes.includes("circles")) {
          this.renderXTPassSegments(pitchLayer, 1, PITCH_WIDTH, PITCH_HEIGHT, this.playsFiltered, firstHomeTeamColor, tooltip, this.type === "player");
          this.renderXTCircles(pitchLayer, 1, PITCH_WIDTH, PITCH_HEIGHT, this.playsFiltered, firstHomeTeamColor, firstHomeTeamColor, tooltip, this.type === "player");
        }

        this.printPlayerXTLegend(uiLayer, renderW, zeroMargin, this.playsFiltered, this.seasons, this.minXT, this.typeOfPlay, this.playerName)

        } else if (this.type === "game") {
          this.renderXTPassSegments(pitchLayer, 1, PITCH_WIDTH, PITCH_HEIGHT, this.playsFiltered, firstHomeTeamColor, tooltip, this.type === "player");
          this.renderXTCircles(pitchLayer, 1, PITCH_WIDTH, PITCH_HEIGHT, this.playsFiltered, firstHomeTeamColor, firstHomeTeamColor, tooltip, this.type === "player");
          this.printGameXTLegend(uiLayer, renderW, zeroMargin, this.plays, this.minXT, this.typeOfPlay);
          this.printGameLegend(uiLayer, renderW, renderH, zeroMargin, firstHomeTeamColor, firstAwayTeamColor, this.game);
          this.printGameBottomLegend(uiLayer, renderW, renderH, zeroMargin, this.plays);
          const rectW = renderW * 0.58;
          const rectH = renderW * 0.1;
          const rectX = renderW / 2 - rectW / 2;
          const rectY = renderH - rectH - renderW * (this.isVertical ? -0.01 : 0.01);
          const gap = renderW * 0.015;
          const logoW = renderW * 0.14;
          const logoH = renderH * 0.07;
          const logoY = rectY + rectH - logoH;
          this.drawImageBase64(uiLayer, rectX + rectW + gap, logoY, logoW, logoH, logo, 'logoDriblab');
          this.printArrowTypeLegend(uiLayer, renderW, renderH, zeroMargin, true);
      }

      let font = await loadFonts(this.fontFamily);
      copyAndAppendElement(svg.node(),font.node(),0,0);

      this.$emit("loaded", this.id);
    },
      measureText(string, fontSize = 10) {
        const widths = [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0.278125,0.278125,0.35625,0.55625,
      		0.55625,0.890625,0.6671875,0.1921875,0.334375,0.334375,0.390625,0.584375,0.278125,0.334375,0.278125,0.303125,0.55625,
      		0.55625,0.55625,0.55625,0.55625,0.55625,0.55625,0.55625,0.55625,0.55625,0.278125,0.278125,0.5859375,0.584375,0.5859375,
      		0.55625,1.015625,0.6671875,0.6671875,0.7234375,0.7234375,0.6671875,0.6109375,0.778125,0.7234375,0.278125,0.5,0.6671875,
      		0.55625,0.834375,0.7234375,0.778125,0.6671875,0.778125,0.7234375,0.6671875,0.6109375,0.7234375,0.6671875,0.9453125,
      		0.6671875,0.6671875,0.6109375,0.278125,0.35625,0.278125,0.478125,0.55625,0.334375,0.55625,0.55625,0.5,0.55625,
      		0.55625,0.278125,0.55625,0.55625,0.2234375,0.2421875,0.5,0.2234375,0.834375,0.55625,0.55625,0.55625,0.55625,
      		0.334375,0.5,0.278125,0.55625,0.5,0.7234375,0.5,0.5,0.5,0.35625,0.2609375,0.3546875,0.590625];
        const avg = 0.5293256578947368;

        if (!string)
          return 0

        return string
          .split('')
          .map(c => c.charCodeAt(0) < widths.length ? widths[c.charCodeAt(0)] : avg)
          .reduce((cur, acc) => acc + cur) * fontSize;
      },
      truncateText(text, fontSize, maxWidth, preserveSuffix) {
        if (this.measureText(text, fontSize) <= maxWidth) return text;
        let suffix = '';
        let main = text;
        if (preserveSuffix) {
          const match = text.match(/ \((\+\d+)\)$/);
          if (match) {
            suffix = ' ' + match[1];
            main = text.slice(0, -match[0].length);
          }
        }
        const suffixW = suffix ? this.measureText(suffix, fontSize) : 0;
        const availW = maxWidth - suffixW;
        if (availW <= 0) return text;
        let t = main;
        while (this.measureText(t + '…', fontSize) + suffixW > maxWidth && t.length > 1) {
          t = t.slice(0, -1);
        }
        return t + '…' + suffix;
      },
      colorScale(minDomain, maxDomain, value, steps) {
          //Range of colors in HSV format gradient
          var colorsArray = ["#fb0e0e","#fb130e","#fb180f","#fa1d0f","#fa220f","#fa270f","#fa2c10","#fa3110","#f93610","#f93b11","#f94011","#f94511","#f84911","#f84e12","#f85312",
          "#f85812","#f85d13","#f76113","#f76613","#f76b13","#f76f14","#f77414","#f67914","#f67d14","#f68215","#f68715","#f58b15","#f59016","#f59416","#f59916","#f59d16","#f4a217",
          "#f4a617","#f4aa17","#f4af17","#f4b318","#f3b818","#f3bc18","#f3c019","#f3c519","#f3c919","#f2cd19","#f2d11a","#f2d61a","#f2da1a","#f1de1a","#f1e21b","#f1e61b","#f1ea1b",
          "#f1ee1b","#eef01c","#eaf01c","#e5f01c","#e1f01d","#dcf01d","#d8ef1d","#d4ef1d","#cfef1e","#cbef1e","#c6ee1e","#c2ee1e","#beee1f","#baee1f","#b5ee1f","#b1ed1f","#aded20",
          "#a9ed20","#a4ed20","#a0ed20","#9cec21","#98ec21","#94ec21","#90ec21","#8cec22","#88eb22","#84eb22","#80eb22","#7ceb23","#78ea23","#74ea23","#70ea23","#6cea24","#68ea24",
          "#64e924","#60e924","#5ce925","#58e925","#54e925","#51e825","#4de826","#49e826","#45e826","#42e726","#3ee727","#3ae727","#37e727","#33e727","#2fe628","#2ce628","#28e628"];

          //Get scale functions.
          // - Range: Number of gradient steps
          // - Domain: Range of values
          let colorScale = d3.scale.linear().domain([minDomain, maxDomain]).range([0, steps - 1]);
          //Get index of colorsArray
          var index = colorScale(value);
          //Get whole part of the index
          var color = colorsArray[Math.trunc(index)];

          return color;
      },
      dist(p1, x, y) {
        let dx = x - p1.x;
        let dy = y - p1.y;
        return Math.sqrt(dx * dx + dy * dy);
      },
      getLengthForPoint(x, y, thePath){
        const self = this;
        let pathLength = thePath.getTotalLength();
        let precision = 100;
        let division = pathLength / precision;

          let theRecord = pathLength;
          let theSegment;
          for (let i = 0; i < precision; i++) {
          // get a point on the path for thia distance
          let _p = thePath.getPointAtLength(i * division);
          // get the distance between the new point _p and the point p
          let theDistance = self.dist(_p, x, y);
          if (theDistance < theRecord) {
          // if the distance is smaller than the record set the new record
            theRecord = theDistance;
            theSegment = i;
          }
        }
        return(theSegment * division);
      },
      transition(path, points) {
        const self = this;
        path.transition()
            .duration(500)
            .attrTween("stroke-dasharray", function() {
              const distanceToPoint = self.getLengthForPoint(points[1][0], points[1][1], this);

              var l = this.getTotalLength(),
                  i = d3.interpolateString("0," + l, distanceToPoint + "," + l);
              return function(t) { return i(t); };
            })
            .each("end", function() { d3.select(this).call( function() { return null; }); });
      },
      renderXTPassSegments(svg, chartSizeRate, chartWidth, chartHeight, segments, teamColor, tooltip, isPlayerXT, isLegend = false) {

        let x = d3.scale.linear().domain([0 - (100 * 0.093), 100 + (100 * 0.1)]).range([chartWidth, 0]);
        let y = d3.scale.linear().domain([0 - (100 * 0.105), 100 + (100 * 0.105)]).range([chartHeight, 0]);
        // ====================================================================
        //        Render Segments
        // ====================================================================
        for (let segment of segments) {
          let sx0;
          let sy0;
          let sx1;
          let sy1;
          let sx2;
          let sy2;

          // THIS is a fix for when a carry comes from the own pitch
          // We cut the line to match the bottom of the SVG so that, when
          // downloading the PNG, the size of the SVG matches the size of the pitch image
          if (segment.x_start < 44.8 && isPlayerXT && !isLegend)
           segment.x_start = 44.8

          if (isPlayerXT) {
            sx0 = x(segment.carry ? segment.y_start : segment.y);
            sy0 = y(segment.carry ? 2 * segment.x_start - 100 : 2 * segment.x - 100);
            sx1 = x(segment.y);
            sy1 = y(2 * segment.x - 100);
            sx2 = x(segment.y_end);
            sy2 = y(2 * segment.x_end - 100);
          } else if (segment.homeTeam) {
            sx0 = x(segment.carry ? segment.x_start : segment.x);
            sy0 = y(segment.carry ? 100 - segment.y_start : 100 - segment.y);
            sx1 = x(segment.x);
            sy1 = y(100 - segment.y);
            sx2 = x(segment.x_end);
            sy2 = y(100 - segment.y_end);
          } else {
            sx0 = x(segment.carry ? 100 - segment.x_start : 100 - segment.x);
            sy0 = y(segment.carry ? segment.y_start : segment.y);
            sx1 = x(100 - segment.x);
            sy1 = y(segment.y);
            sx2 = x(100 - segment.x_end);
            sy2 = y(segment.y_end);
          }
          if (segment.type === "PASS") {
            this.drawCarryAndPassLine(svg, chartSizeRate, segment, sx0, sy0, sx1, sy1, sx2, sy2, chartWidth, segment.xT);

            var color = "green";
            var triangleSize = chartWidth * 0.04;
            const angleRadians = Math.atan2(sy2 - sy1, sx2 - sx1);

            var verticalTransform = (angleRadians + (Math.PI / 2)) * (180/Math.PI); // To 180 and from radians to degrees

            var triangle = d3.svg.symbol()
              .type("triangle-up")
              .size(triangleSize)

            svg.append("path")
            .attr("d", triangle)
            .attr("stroke", this.colorScale(0.4, 0, segment.xT > 0.4 ? 0.4 : segment.xT, 100))
            .attr("fill", this.colorScale(0.4, 0, segment.xT > 0.4 ? 0.4 : segment.xT, 100))
            .attr("transform", function(d) { return "translate(" + sx2 + "," + sy2 + ") rotate(" + verticalTransform + ")"; });

           } else {
             this.drawXTLine(svg, chartSizeRate, segment, sx0, sy0, sx1, sy1, chartWidth, segment.xT, tooltip, teamColor, 0.8, true);
           }
        }
      },
      async drawImageBase64(svg,x,y,width,height,img,idName,bottom = false) {
        return new Promise((resolve) => {
          if(!this.images[idName]){
            let base_image = new Image();
            base_image.crossOrigin = "anonymous";
            base_image.src = img;
            base_image.onload = async (event) => {
              const dataUrl = await getDataUrl(event.currentTarget);
              this.drawImage(
              svg,
              x,
              y,
              width,
              height,
              dataUrl,
              idName,
              bottom
              );
              resolve(true);
            }
          }else{
            if(bottom){
              svg.node().insertBefore(this.images[idName],svg.node().firstChild);
            }else{
              svg.node().appendChild(this.images[idName]);
            }
            resolve(true);
          }
        })
      },
      drawImage: function(svg, x, y, width, height, img,idName,bottom = false) {
        let imgNode = bottom ? svg.insert("svg:image",':first-child') : svg.append("svg:image");
          imgNode.attr("x", x)
          .attr("y", y)
          .attr("width", width)
          .attr("height", height)
          .attr("xlink:href", img);
        this.images[idName] = imgNode.node().cloneNode(true);
      },
      drawRectangle(svg, x, y, width, height, color, opacity, className) {
        return svg.append("rect")
          .attr("class", className)
          .attr("x", x)
          .attr("y", y)
          .attr("width", width)
          .attr("height", height)
          .style("fill", color)
          .style("opacity", opacity);
      },
      writeText(svg, x, y, anchor, fontSize, text, fill = "white", className = "none") {
        svg.append("text")
          .attr("x", x)
          .attr("y", y)
          .attr("text-anchor", anchor)
          .attr("dy", "0.8em")
          .attr("font-weight", 500)
          .attr("class", className)
          .style("font-size", fontSize)
          .style("font-family", this.fontFamily)
          .style("fill", fill)
          .text(text);
      },
      getTypeOfEvent(name) {
        if (name === "PASS") return "Pass";
        if (name === "FOUL") return "Foul";
        if (name === "AERIAL") return "Aerial";
        if (name === "SAVED SHOT") return "Saved Shot";
        if (name === "GOAL") return "Goal";
        if (name === "SHOT ON POST") return "Shot on Post";
        if (name === "BALL RECOVERY") return "Ball Recovery";
        if (name === "INTERCEPTION") return "Interception";
        if (name === "TAKE ON") return "Dribble";
        if (name === "MISSED SHOT") return "Missed Shot";

      	return name;
      },
      isAshot(name) {
        if (name === "SAVED SHOT") return true;
        if (name === "GOAL") return true;
        if (name === "SHOT ON POST") return true;
        if (name === "MISSED SHOT") return true;
        return false;
      },
      drawCircle(svg, x, y, radius, fillColor,borderColor,borderSize, className) {
        return svg.append("circle")
          .attr("class", className)
          .attr("r", radius)
          .attr("cx", x)
          .attr("cy", y)
          .style("fill", fillColor)
          .style("stroke", borderColor)
          .style("stroke-width", radius / 5)
          .style("fill-opacity", 0.6)
          .style("opacity", 0.8);
      },
      drawXTCircle(svg, chartSizeRate, chartWidth, x, y, circle, i, radius, xt, tooltip) {
        var self = this;
        svg.append("circle")
          .attr("r", radius * chartSizeRate)
          .attr("cx", x)
          .attr("cy", y)
      		.attr('id', "pass" + i + "-" + circle.teamName + "-" + circle.playerName)
          .style("fill-opacity", 0.6)
          .style("opacity", 0.8)
          .style("stroke",  this.colorScale(0.4, 0, xt > 0.4 ? 0.4 : xt, 100))
          .style("stroke-width", 2 * chartWidth * 0.001)
          .style("fill", this.colorScale(0.4, 0, xt > 0.4 ? 0.4 : xt, 100))
      		.on('mouseenter',function(d) {
      			d3.select(this)
      				.transition()
      				.duration(300)
              .style("cursor", "pointer")
              .attr('r', radius * 1.5);

            var textLine1 = circle.game;
            var textLine2 = circle.strategy.endsWith("play") ? Dictionary[self.language]["game"]["gameThreat"][_.camelCase(circle.strategy)] : Dictionary[self.language]["game"]["gameThreat"][_.camelCase(circle.strategy + " play")];
            if (!textLine2)
              textLine2 = Dictionary[self.language]["game"]["gameThreat"]["regularPlay"]
            var textLine3 = "Carry and Pass ";
            if (self.isAshot(circle.type))
              textLine3 = "Shot ";
            else if (Math.abs(circle.x - circle.x_start) < 5 && Math.abs(circle.y - circle.y_start) < 5)
              textLine3 = "Pass ";
            else if (circle.type !== "PASS")
              textLine3 = "Carry ";

            textLine3 = Dictionary[self.language]["game"]["gameThreat"][_.camelCase('legend'+textLine3)]

            textLine3 = textLine3 + " at " +  + circle.min + ":" + (circle.sec < 10 ? "0" + circle.sec : circle.sec) +  " · <b>" + (circle.xT.toFixed(3) * 1) + " xT</b>";

            var textLine4 = self.getTypeOfEvent(circle.type) +  ( circle.type === "FOUL" ? " to " : " of ") + circle.player + ( circle.playerTo && !self.isAshot(circle.type) && circle.type !== "FOUL"  && circle.type !== "BALL RECOVERY" ? " to " + circle.playerTo : "");
            var maxTextWidth = Math.max(self.measureText(textLine1, 14), self.measureText(textLine2, 14), self.measureText(textLine3, 14), self.measureText(textLine4, 14));
            var widthTooltip = maxTextWidth + 60;
            var height = 90;

      			var shotId = document.getElementById("pass" + i + "-" + circle.teamName + "-" + circle.playerName );
            var circleRadio = Math.ceil(shotId.getAttribute('r'));
            let tooltipX = self.$vuetify.breakpoint.mobile ? (window.innerWidth/2) - (widthTooltip/2): shotId.getBoundingClientRect().left + circleRadio - widthTooltip / 2
            let tooltipY = self.$vuetify.breakpoint.mobile ? d3.select(`#${self.rootElementId}`).node().getBoundingClientRect().top + window.scrollY + (height/2) : (shotId.getBoundingClientRect().top + window.scrollY +  circleRadio - height - 50)

      			d3.select("body > .tooltip")
              .style("width", widthTooltip + "px")
              .style("height", height + "px")
      				.style("left", tooltipX + "px")
      				.style("top", tooltipY + "px")
      				.style("display", "inline-block")
              .style("font-size", "14px")
      				.html("<b>" + circle.game + "</b></br>" +
                    textLine2 + "</br>" +
                    textLine3 + "</br>" +
                    textLine4);
      		})
      		.on('mouseout',function() {
            d3.select(this)
              .transition()
              .duration(200)
              .attr('r', radius);
      			d3.select("body > .tooltip").style("display", "none");
      		});
      },
      drawXTLine(svg, chartSizeRate, segment, x0, y0, x1, y1, width, xt, tooltip, strokeColor = "black", opacity = 1, dashed = false) {
        var self = this;
    		svg.append("line")
         .attr("x1", x0)
         .attr("y1", y0)
         .attr("x2", x1)
         .attr("y2", y1)
         .attr('id', "pass" + segment.numberOfPlay + "-" + segment.teamName)
         .attr("stroke-width", 0.002 * width)
         .style("stroke-dasharray", 3 * chartSizeRate) //dashed array for line
    		 .style("opacity", 1)
         .attr("stroke", self.colorScale(0.4, 0, xt > 0.4 ? 0.4 : xt, 100))
      },
      drawCarryAndPassLine(svg, chartSizeRate, segment, sx0, sy0, sx1, sy1, sx2, sy2, width, xt) {
        svg.append("line")
          .attr("x1", sx1)
          .attr("y1", sy1)
          .attr("x2", sx2)
          .attr("y2", sy2)
          .attr('id', "pass" + segment.numberOfPlay + "-" + segment.teamName)
          .attr("stroke-width", 0.002 * width)
          .attr("stroke", this.colorScale(0.4, 0, xt > 0.4 ? 0.4 : xt, 100))
          .style("opacity", 1);

          svg.append("line")
            .attr("x1", sx0)
            .attr("y1", sy0)
            .attr("x2", sx1)
            .attr("y2", sy1)
            .attr('id', "pass" + segment.numberOfPlay + "-" + segment.teamName)
            .attr("stroke-width", 0.002 * width)
            .attr("stroke", this.colorScale(0.4, 0, xt > 0.4 ? 0.4 : xt, 100))
            .style("opacity", 1)
            .style("stroke-dasharray", 3 * chartSizeRate);

      },
      renderXTCircles(svg, chartSizeRate, chartWidth, chartHeight, circles, teamColor, strokeColor, tooltip, isPlayer) {

        let x = d3.scale.linear().domain([0 - (100 * 0.093), 100 + (100 * 0.1)]).range([chartWidth, 0]);
        let y = d3.scale.linear().domain([0 - (100 * 0.105), 100 + (100 * 0.105)]).range([chartHeight, 0]);

        let radius = d3.scale.linear().domain([0, 1]).range([chartWidth * 0.0055, chartWidth * 0.024]);
        for (let i in circles) {
          let sx;
          let sy;
          if (isPlayer) {
            sx = x(circles[i].y);
            sy = y(2 * circles[i].x - 100);
          } else if (circles[i].xT) {
            sx = circles[i].homeTeam ? x(circles[i].x) : x(100 - circles[i].x);
            sy = circles[i].homeTeam ? y(100 - circles[i].y) : y(circles[i].y);
          } else {
            sx = circles[i].homeTeam ? x(circles[i].x) : x(100 - circles[i].x);
            sy = circles[i].homeTeam ? y(100 - circles[i].y) : y(circles[i].y);
          }
          this.drawXTCircle(svg, chartSizeRate, chartWidth, sx, sy, circles[i], i, radius(circles[i].xT), circles[i].xT, tooltip);
        }
      },
      getPlayersWithHighestXT(shots) {
        // Group players by name
        const sumXTShots = shots.reduce(function(acc, val){
          var o = acc.filter(function(obj){
              return obj.player === val.player;
          }).pop() || {player: val.player, xT: 0};

          o.xT += val.xT;
          const index = acc.findIndex(p => p.player === o.player)
          if (index === -1)
            acc.push(o);
          else
            acc[index] = o;
          return acc;
        },[]);
        // Sort, take out players with xT < 0.1 and return first three
        return sumXTShots.sort((a, b) => b.xT - a.xT).filter(p => p.xT >= 0.05).slice(0, 3);
      },
      printGameLegend(svg, pitchSize, pitchHeight, margin, homeTeamColor, awayTeamColor, gameData) {
        let textColor = this.currentUserFieldColor === 'white' ? 'black' : 'white';
        let secondHomeTeamColor = this.homeBorderColor;
        let neutralColorsAway = getNeutralColors('away');
        let secondAwayTeamColor = this.game ? neutralColorsAway[1] : this.awayBorderColor;

        let homeScore = this.game.homeTeamScore;
        let awayScore = this.game.awayTeamScore;
        
        let homeTeamCrestXFactor;
        if (this.isVertical) {
          homeTeamCrestXFactor = this.isMobile ? 0.12 : 0.08;
        }
        else {
          homeTeamCrestXFactor = this.isMobile ? 0.09 : 0.07;
        }
        
        let homeTeamNameXFactor;
        if (this.isVertical) {
          homeTeamNameXFactor = this.isMobile ? 0.18 : 0.14;
        }
        else {
          homeTeamNameXFactor = this.isMobile ? 0.13 : 0.115;
        }
        let homeCrestX = homeTeamCrestXFactor * pitchSize + margin.left

        let rectHome = this.drawRectangle(svg,
          homeCrestX - 16,
          margin.top - 8,
          16,
          (0.075 * pitchHeight) + 5,
          homeTeamColor.replace("0x", "#"), 0.9, "gameLegend1");
                  rectHome
        .style("border-radius",'3px')
        .style("border",`1px solid ${secondHomeTeamColor}`)
        .style("opacity",0.95)
        .style("stroke-width",1)
        .style("stroke",`${secondHomeTeamColor}`)
        .attr("rx",3)
        .attr("ry",3)


        this.drawImageBase64(svg,
          homeTeamCrestXFactor * pitchSize + margin.left,
          (this.isVertical ? 0.03 : 0.02) * pitchHeight + margin.top,
          0.05 * pitchSize,
          0.05 * pitchHeight,
          'https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/' + gameData.homeTeamId + '.png', 'homeCrest');
        this.writeText(svg,
          homeTeamNameXFactor * pitchSize + margin.left,
          (this.isVertical ? 0.05 : 0.03) * pitchHeight + margin.top,
          "start",
          ((this.isVertical ? 0.024 : 0.022) * pitchSize) + "px",
          `${ this.homeTeamName } • ` + homeScore, textColor);

        let rectAway = this.drawRectangle(svg,
          0.935 * pitchSize + margin.left,
          margin.top - 8,
          16,
          (0.075 * pitchHeight) + 5,
          awayTeamColor.replace("0x", "#"), 0.9, "gameLegend1");
        rectAway
        .style("border-radius",'3px')
        .style("border",`1px solid ${secondAwayTeamColor}`)
        .style("opacity",0.95)
        .style("stroke-width",1)
        .style("stroke",`${secondAwayTeamColor}`)
        .attr("rx",3)
        .attr("ry",3)

        this.drawImageBase64(svg,
          (this.isVertical ? 0.875 : 0.885) * pitchSize + margin.left,
          (this.isVertical ? 0.03 : 0.02) * pitchHeight + margin.top,
          0.05 * pitchSize,
          0.05 * pitchHeight,
          'https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/' + gameData.awayTeamId + '.png', 'awayCrest');
        this.writeText(svg,
          (this.isVertical ? 0.865 : 0.89) * pitchSize + margin.left,
          (this.isVertical ? 0.05 : 0.03) * pitchHeight + margin.top,
          "end",
          ((this.isVertical ? 0.024 : 0.022) * pitchSize) + "px",
          awayScore + ` • ${ this.awayTeamName }`, textColor);
      },
      printArrowTypeLegend(svg, pitchWidth, pitchHeight, margin, isGame = false) {
        const isDark = this.currentUserFieldColor !== 'white';
        const textColor = isDark ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.8)';
        const lineColor = isDark ? '#28e628' : 'rgba(0,0,0,0.65)';
        const sizeScale = isGame ? 1 : 1.5;
        const colGapScale = isGame ? 1 : 0.65;
        const rowGapScale = isGame ? 1 : 1.4;
        const fontSize = pitchWidth * 0.009 * sizeScale;
        const lineLen = pitchWidth * 0.028 * sizeScale;
        const dashLen = pitchWidth * 0.0025 * sizeScale;
        const lineW = pitchWidth * 0.0018 * sizeScale;
        const midCircleR = pitchWidth * 0.0028 * sizeScale;
        const endCircleR = pitchWidth * 0.003 * sizeScale;
        const triangleSize = pitchWidth * 0.011 * sizeScale;
        const lineToTextGap = pitchWidth * 0.005 * sizeScale;
        const colGap = pitchWidth * 0.001 * colGapScale;
        const rowGap = pitchWidth * 0.012 * rowGapScale;
        const symbolW = lineLen + triangleSize + pitchWidth * 0.003 * sizeScale;

        const gameThreatDict = Dictionary[this.language]["game"]["gameThreat"];
        const items = [
          { label: gameThreatDict["legendPass"] || 'Pass', type: 'pass' },
          { label: gameThreatDict["legendCarryAndPass"] || 'Carry and Pass', type: 'carryAndPass' },
          { label: gameThreatDict["legendCarryAndAction"] || 'Carry and Action', type: 'carryAndAction' },
        ];

        const drawLegendItem = (cx, cy, item) => {
          const arrowOffset = pitchWidth * 0.002;
          if (item.type === 'pass') {
            svg.append("line")
              .attr("x1", cx).attr("y1", cy)
              .attr("x2", cx + lineLen).attr("y2", cy)
              .attr("stroke", lineColor).attr("stroke-width", lineW);
            const triangle = d3.svg.symbol().type("triangle-up").size(triangleSize);
            svg.append("path")
              .attr("d", triangle).attr("fill", lineColor).attr("stroke", lineColor)
              .attr("transform", `translate(${cx + lineLen + arrowOffset}, ${cy}) rotate(90)`);
          } else if (item.type === 'carryAndAction') {
            svg.append("line")
              .attr("x1", cx).attr("y1", cy)
              .attr("x2", cx + lineLen).attr("y2", cy)
              .attr("stroke", lineColor).attr("stroke-width", lineW)
              .style("stroke-dasharray", dashLen);
            svg.append("circle")
              .attr("cx", cx + lineLen).attr("cy", cy)
              .attr("r", endCircleR)
              .style("fill", lineColor).style("fill-opacity", 0.4)
              .style("stroke", lineColor).style("stroke-width", lineW);
          } else {
            const circleCenterX = cx + lineLen * 0.42;
            const solidEndX = cx + lineLen;
            svg.append("line")
              .attr("x1", cx).attr("y1", cy)
              .attr("x2", circleCenterX - midCircleR).attr("y2", cy)
              .attr("stroke", lineColor).attr("stroke-width", lineW)
              .style("stroke-dasharray", dashLen);
            svg.append("circle")
              .attr("cx", circleCenterX).attr("cy", cy)
              .attr("r", midCircleR)
              .style("fill", lineColor).style("fill-opacity", 0.6)
              .style("stroke", lineColor).style("stroke-width", lineW);
            svg.append("line")
              .attr("x1", circleCenterX + midCircleR).attr("y1", cy)
              .attr("x2", solidEndX).attr("y2", cy)
              .attr("stroke", lineColor).attr("stroke-width", lineW);
            const triangle = d3.svg.symbol().type("triangle-up").size(triangleSize);
            svg.append("path")
              .attr("d", triangle).attr("fill", lineColor).attr("stroke", lineColor)
              .attr("transform", `translate(${solidEndX + arrowOffset}, ${cy}) rotate(90)`);
          }
        };

        if (isGame) {
          const rectW = pitchWidth * 0.40;
          const rectH = pitchWidth * 0.1;
          const rectX = (pitchWidth / 2 - rectW / 2) + margin.left;
          const rectY = pitchHeight + margin.top - rectH - pitchWidth * 0.01;
          const gap = pitchWidth * 0.012;
          const itemW = symbolW + lineToTextGap + pitchWidth * 0.03;
          const legendTop = rectY + rectH / 2 - rowGap + pitchWidth * 0.025;
          const symbolStartX = rectX - gap - itemW * 2 - colGap - pitchWidth * 0.12;

          const addItem = (ix, iy, item) => {
            drawLegendItem(ix, iy, item);
            svg.append("text")
              .attr("x", ix + symbolW + lineToTextGap)
              .attr("y", iy)
              .attr("text-anchor", "start")
              .attr("dominant-baseline", "central")
              .style("font-size", fontSize + "px")
              .style("font-family", this.fontFamily)
              .style("fill", textColor)
              .style("font-weight", 400)
              .text(item.label);
          };

          const row1Y = legendTop + rowGap / 2;
          const row2Y = legendTop + rowGap + rowGap / 2;
          addItem(symbolStartX, row1Y, items[0]);
          addItem(symbolStartX + itemW + colGap, row1Y, items[1]);
          addItem(symbolStartX + itemW + colGap, row2Y, items[2]);
        } else {
          const symbolStartX = margin.left + pitchWidth * 0.08;
          const legendTop = margin.top + pitchWidth * 0.02;
          const itemW = symbolW + lineToTextGap + pitchWidth * 0.06;

          const addItem = (ix, iy, item) => {
            drawLegendItem(ix, iy, item);
            svg.append("text")
              .attr("x", ix + symbolW + lineToTextGap)
              .attr("y", iy)
              .attr("text-anchor", "start")
              .attr("dominant-baseline", "central")
              .style("font-size", fontSize + "px")
              .style("font-family", this.fontFamily)
              .style("fill", textColor)
              .style("font-weight", 400)
              .text(item.label);
          };

          const row1Y = legendTop + rowGap / 2;
          const row2Y = legendTop + rowGap + rowGap / 2;
          addItem(symbolStartX, row1Y, items[0]);
          addItem(symbolStartX + itemW + colGap, row1Y, items[1]);
          addItem(symbolStartX + itemW + colGap, row2Y, items[2]);
        }
      },
      printPlayerXTLegend(svg, pitchWidth, margin, shots, seasons, minXT, typeOfPlay, playerName) {
        const xT = shots.map(shot => shot.xT).reduce((a, b) => a + b, 0);
        const playerShots = shots.length;
        const noGamesSelected = this.gameIds && this.gameIds.length === 0;
        const games = noGamesSelected ? 0 : (this.gameIds.length ? this.gameIds.length : seasons.map(s => s.gamesPlayed).reduce((a, b) => a + b, 0));
        const minutes = noGamesSelected ? 0 : (this.minsPlayed > 0 ? this.minsPlayed : seasons.map(s => s.minutesPlayed).reduce((a, b) => a + b, 0));
        let xTDecimals = 1;

        const rectW = 0.72 * pitchWidth;
        const rectH = 0.155 * pitchWidth;
        const rectX = (pitchWidth / 2 - rectW / 2) + margin.left;
        const rectY = pitchWidth * 0.59 + margin.top;

        this.drawRectangle(svg, rectX, rectY, rectW, rectH, "#1a1a1a", 0.92, "xtPlayerLegendRect");

        const playsLabel = Dictionary[this.language]["game"]["gameThreat"]["legendPlays"];
        const xTLabel = Dictionary[this.language]["game"]["gameThreat"]["xT"];

        const crestTeamId = this.teamId || (seasons.length > 0 ? seasons[0].teamId : 0);
        const baseCrestSize = pitchWidth * 0.054;
        const nameGap = pitchWidth * 0.014;
        const baseNameFontSize = 0.026 * pitchWidth;
        const nameWidth = this.measureText(playerName || "", baseNameFontSize);

        const seasonDisplayMode = 'two-line';
        const hasSecondSeasonLine = seasonDisplayMode === 'two-line' && !(this.gameId > 0 || this.gameName) && seasons.length >= 3;
        const crestSize = hasSecondSeasonLine ? pitchWidth * 0.068 : baseCrestSize;

        const heroFontSizeCalc = pitchWidth * 0.025;
        const heroTextCalc = `${ playerShots } ${ playsLabel }  ·  ${ xT.toFixed(xTDecimals) } xT`;
        const heroWidthCalc = this.measureText(heroTextCalc, heroFontSizeCalc);

        const rawNameLineW = crestSize + nameGap + nameWidth;
        const rawHeroLineW = heroWidthCalc;
        const rawContentW = Math.max(rawNameLineW, rawHeroLineW);
        const minDividerX = rectX + rectW * 0.42;
        const maxDividerX = rectX + rectW * 0.55;
        const dividerX = Math.min(maxDividerX, Math.max(minDividerX, rectX + rawContentW + pitchWidth * 0.04));

        const leftAvailable = dividerX - rectX;
        const leftPad = Math.max(pitchWidth * 0.02, (leftAvailable - rawContentW) / 2);

        const leftContentH = crestSize + pitchWidth * 0.012 + pitchWidth * 0.030 + pitchWidth * 0.014 + (hasSecondSeasonLine ? pitchWidth * 0.010 : 0);
        const leftTopY = rectY + (rectH - leftContentH) / 2;
        const crestY = leftTopY;

        if (crestTeamId) {
          this.drawImageBase64(svg, rectX + leftPad, crestY, crestSize, crestSize,
            'https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/' + crestTeamId + '.png', 'xtPlayerCrest');
        }

        const nameX = rectX + leftPad + crestSize + nameGap;
        const nameY = crestY + pitchWidth * 0.004;

        svg.append("text")
          .attr("x", nameX)
          .attr("y", nameY)
          .attr("text-anchor", "start")
            .attr("dy", "0.8em")
          .attr("font-weight", 600)
          .style("font-size", baseNameFontSize + "px")
            .style("font-family", this.fontFamily)
            .style("fill", "white")
          .attr("class", "playerXTName")
          .text(playerName);

        const seasonFontSize = pitchWidth * 0.015;
        const seasonLineH = pitchWidth * 0.016;
        const maxSeasonWidth = dividerX - nameX - pitchWidth * 0.01;
        let seasonLines = [];

        if (this.gameId > 0 || this.gameName) {
          seasonLines.push(this.gameName);
          xTDecimals = 2;
        } else if (seasons.length > 0) {
          const names = seasons.map(s => s.seasonAlias || s.seasonName);

          if (names.length === 1) {
            seasonLines.push(this.truncateText(names[0], seasonFontSize, maxSeasonWidth));
          } else {
            const prefix1 = names[0] + ', ';
            const prefixW1 = this.measureText(prefix1, seasonFontSize);
            const line1 = prefix1 + this.truncateText(names[1], seasonFontSize, maxSeasonWidth - prefixW1);
            seasonLines.push(line1);

            if (names.length >= 3) {
              const extra = names.length > 4 ? ` +${names.length - 4}` : '';
              const availW2 = extra ? maxSeasonWidth - this.measureText(extra, seasonFontSize) : maxSeasonWidth;
              if (names.length >= 4) {
                const prefix2 = names[2] + ', ';
                const prefixW2 = this.measureText(prefix2, seasonFontSize);
                const line2 = prefix2 + this.truncateText(names[3], seasonFontSize, availW2 - prefixW2) + extra;
                seasonLines.push(line2);
              } else {
                const line2 = this.truncateText(names[2], seasonFontSize, availW2) + extra;
                seasonLines.push(line2);
              }
            }
          }
        }

        seasonLines.forEach((line, i) => {
          svg.append("text")
            .attr("x", nameX)
            .attr("y", nameY + pitchWidth * 0.030 + i * seasonLineH)
            .attr("text-anchor", "start")
            .attr("dy", "0.8em")
            .attr("font-weight", 400)
            .style("font-size", seasonFontSize + "px")
            .style("font-family", this.fontFamily)
            .style("fill", "rgba(255,255,255,0.85)")
            .attr("class", "seasonText")
            .text(line);
        });

        const heroFontSize = pitchWidth * 0.025;
        const heroY = crestY + crestSize + pitchWidth * 0.008 + (hasSecondSeasonLine ? pitchWidth * 0.010 : 0);
        const heroLine = `${ playerShots } ${ playsLabel }  ·  ${ xT.toFixed(xTDecimals) } ${ xTLabel }`;

        svg.append("text")
          .attr("x", rectX + leftPad)
          .attr("y", heroY)
          .attr("text-anchor", "start")
          .attr("dy", "0.8em")
          .attr("font-weight", 700)
          .style("font-size", heroFontSize + "px")
          .style("font-family", this.fontFamily)
          .style("fill", "white")
          .attr("class", "playerXTandPlays")
          .text(heroLine);

        const footnoteFontSize = pitchWidth * 0.011;
        const footnoteY = rectY + rectH - pitchWidth * 0.018;
        const legendOnlyShowing = Dictionary[this.language]["game"]["gameThreat"]["legendOnlyShowing"] || 'Only showing individual actions with xT > ';
        svg.append("text")
          .attr("x", rectX + pitchWidth * 0.012)
          .attr("y", footnoteY)
          .attr("text-anchor", "start")
          .attr("dy", "0.8em")
          .attr("font-weight", 400)
          .style("font-size", footnoteFontSize + "px")
          .style("font-family", this.fontFamily)
          .style("fill", "rgba(255,255,255,0.8)")
          .text(`* ${legendOnlyShowing}${minXT}`);

        svg.append("line")
          .attr("x1", dividerX)
          .attr("y1", rectY + pitchWidth * 0.010)
          .attr("x2", dividerX)
          .attr("y2", rectY + rectH - pitchWidth * 0.010)
          .attr("stroke", "rgba(255,255,255,0.5)")
          .attr("stroke-width", 1);

        const rightSectionW = rectX + rectW - dividerX;
        const rightPad = pitchWidth * 0.018;
        const rightX = dividerX + rightPad;
        const col2X = dividerX + rightSectionW * 0.36;
        const col3X = dividerX + rightSectionW * 0.66;

        const labelFontSize = pitchWidth * 0.012;
        const valueFontSize = pitchWidth * 0.019;
        const rowH = pitchWidth * 0.042;
        const totalRows = 2;
        const rightContentH = totalRows * rowH;
        const startY = rectY + (rectH - rightContentH) / 2;

        const gamesLabel = Dictionary[this.language]["table"]["history"]["games"] + ' / ' + Dictionary[this.language]["table"]["roster"]["minutes"];
        const gamesValue = (games === 1 && this.gameName)
          ? this.gameName
          : `${games} / ${minutes}`;
        const xTPerPlay = playerShots > 0 ? (xT / playerShots).toFixed(xTDecimals + 1) : '0.00';
        const statsCol1 = [
          { label: gamesLabel, value: gamesValue },
          { label: playsLabel, value: `${playerShots}` },
        ];

        statsCol1.forEach((stat, i) => {
          const y = startY + i * rowH;
          svg.append("text")
            .attr("x", rightX).attr("y", y)
            .attr("text-anchor", "start").attr("dy", "0.8em")
            .attr("font-weight", 400)
            .style("font-size", labelFontSize + "px")
            .style("font-family", this.fontFamily)
            .style("fill", "rgba(255,255,255,0.7)")
            .text(stat.label);
          svg.append("text")
            .attr("x", rightX).attr("y", y + pitchWidth * 0.015)
            .attr("text-anchor", "start").attr("dy", "0.8em")
            .attr("font-weight", 600)
            .style("font-size", valueFontSize + "px")
            .style("font-family", this.fontFamily)
            .style("fill", "white")
            .text(stat.value);
        });

        const xTPer90 = minutes > 0 ? (xT / minutes * 90.0).toFixed(xTDecimals + 1) : '0.0';
        const xTPerPlayLabel = 'xT / ' + Dictionary[this.language]["game"]["gameThreat"]["legendPerPlay"];
        const statsCol2 = [
          { label: xTPerPlayLabel, value: xTPerPlay },
          { label: Dictionary[this.language]["game"]["gameThreat"]["xTPer90"], value: xTPer90 }
        ];

        statsCol2.forEach((stat, i) => {
          const y = startY + i * rowH;
          svg.append("text")
            .attr("x", col2X).attr("y", y)
            .attr("text-anchor", "start").attr("dy", "0.8em")
            .attr("font-weight", 400)
            .style("font-size", labelFontSize + "px")
            .style("font-family", this.fontFamily)
            .style("fill", "rgba(255,255,255,0.7)")
            .text(stat.label);
          svg.append("text")
            .attr("x", col2X).attr("y", y + pitchWidth * 0.015)
            .attr("text-anchor", "start").attr("dy", "0.8em")
            .attr("font-weight", 600)
            .style("font-size", valueFontSize + "px")
            .style("font-family", this.fontFamily)
            .style("fill", "white")
            .text(stat.value);
        });

        const allLabel = Dictionary[this.language]["general"] && Dictionary[this.language]["general"]["all"] ? Dictionary[this.language]["general"]["all"] : 'All';
        const maxFilterWidth = rectX + rectW - col3X - pitchWidth * 0.01;
        const typeOfActionLabel = Dictionary[this.language]["game"]["gameThreat"]["typeOfPlay"] || 'Type of Action';
        const actionEndingLabel = Dictionary[this.language]["game"]["gameThreat"]["actionEnding"] || 'Action Ending';
        const typeOfActionValue = typeOfPlay.length === 2 ? allLabel : (typeOfPlay[0] === 'Set Piece' ? Dictionary[this.language]["game"]["gameThreat"]["setPiece"] : Dictionary[this.language]["game"]["gameThreat"]["regularPlay"]);
        const filtersData = [
          { label: typeOfActionLabel, value: typeOfActionValue },
          { label: actionEndingLabel, value: this.filterActionEndingText || allLabel },
        ];

        filtersData.forEach((filter, i) => {
          const y = startY + i * rowH;
          svg.append("text")
            .attr("x", col3X).attr("y", y)
            .attr("text-anchor", "start").attr("dy", "0.8em")
            .attr("font-weight", 400)
            .style("font-size", labelFontSize + "px")
            .style("font-family", this.fontFamily)
            .style("fill", "rgba(255,255,255,0.7)")
            .text(filter.label);
          const preservePlusN = / \(\+\d+\)$/.test(filter.value);
          svg.append("text")
            .attr("x", col3X).attr("y", y + pitchWidth * 0.015)
            .attr("text-anchor", "start").attr("dy", "0.8em")
            .attr("font-weight", 600)
            .style("font-size", valueFontSize + "px")
            .style("font-family", this.fontFamily)
            .style("fill", "white")
            .text(this.truncateText(filter.value, valueFontSize, maxFilterWidth, preservePlusN));
        });

        const minutesRangeText = this.filterMinutesText || this.filterMinutesRange;
        if (minutesRangeText) {
          const bottomInfoY = rectY + rectH - pitchWidth * 0.018;
          const bottomInfoFontSize = pitchWidth * 0.011;
          svg.append("text")
            .attr("x", rectX + rectW - pitchWidth * 0.012)
            .attr("y", bottomInfoY)
            .attr("text-anchor", "end")
            .attr("dy", "0.8em")
            .attr("font-weight", 400)
            .style("font-size", bottomInfoFontSize + "px")
            .style("font-family", this.fontFamily)
            .style("fill", "white")
            .text(minutesRangeText);
        }
      },
      printGameXTLegend(svg, pitchWidth, margin, shots, minXT, typeOfPlay) {
        let homeShots = shots.filter(s => s.homeTeam);
        let awayShots = shots.filter(s => !s.homeTeam);

        let homeXT = homeShots.map(shot => shot.xT).reduce((a, b) => a + b, 0);
        let awayXT = awayShots.map(shot => shot.xT).reduce((a, b) => a + b, 0);

        const rectangleSize = 0.28 * pitchWidth;
        let xtLegend = this.currentUserLanguage === 'en' ? "Sum of xT" : "Suma de xT";

        this.drawRectangle(svg,
          (pitchWidth / 2 - rectangleSize / 2) + margin.left,
          margin.top,
          rectangleSize,
          rectangleSize * 0.28,
          "black",
          0.85,
          "xtLegendRect");
        this.writeText(svg, pitchWidth / 2 + margin.left, 0.008 * pitchWidth + margin.top, "middle", (0.018 * pitchWidth) + "px", xtLegend);
        const valY = 0.032 * pitchWidth + margin.top;
        const valFont = (0.035 * pitchWidth) + "px";
        const cx = pitchWidth / 2 + margin.left;
        this.writeText(svg, cx, valY, "middle", valFont,
          `${homeXT.toFixed(1)} · ${awayXT.toFixed(1)}`);
      },
      printGameBottomLegend(svg, pitchWidth, pitchHeight, margin, shots) {
        const homeShots = shots.filter(s => s.homeTeam);
        const awayShots = shots.filter(s => !s.homeTeam);

        const homeXT = homeShots.map(s => s.xT).reduce((a, b) => a + b, 0);
        const awayXT = awayShots.map(s => s.xT).reduce((a, b) => a + b, 0);
        const homePlayCount = homeShots.length;
        const awayPlayCount = awayShots.length;
        const homeXTPerPlay = homePlayCount > 0 ? (homeXT / homePlayCount) : 0;
        const awayXTPerPlay = awayPlayCount > 0 ? (awayXT / awayPlayCount) : 0;

        const homeXTPlayers = this.getPlayersWithHighestXT(homeShots);
        const awayXTPlayers = this.getPlayersWithHighestXT(awayShots);

        const rectW = pitchWidth * 0.58;
        const rectH = pitchWidth * 0.1;
        const rectX = (pitchWidth / 2 - rectW / 2) + margin.left;
        const rectY = pitchHeight + margin.top - rectH - pitchWidth * 0.01;

        this.drawRectangle(svg, rectX, rectY, rectW, rectH, "#1a1a1a", 0.92, "gameBottomRect");

        const colPad = pitchWidth * 0.012;
        const centerX = rectX + rectW / 2;

        const vDivLeftX = centerX - pitchWidth * 0.12;
        const vDivRightX = centerX + pitchWidth * 0.12;

        const playerFont = (pitchWidth * 0.012) + "px";
        const topY = rectY + pitchWidth * 0.01;
        const playerNameW = pitchWidth * 0.1;
        const playerXTOffset = pitchWidth * 0.13;

        const homePlayerStart = vDivLeftX - playerXTOffset - pitchWidth * 0.015;
        const awayPlayerStart = vDivRightX + pitchWidth * 0.015;

        homeXTPlayers.forEach((p, i) => {
          const y = topY + i * pitchWidth * 0.017;
          this.writeText(svg, homePlayerStart, y, "start", playerFont, this.truncateText(p.player, pitchWidth * 0.012, playerNameW), "white");
          this.writeText(svg, homePlayerStart + playerXTOffset, y, "end", playerFont, p.xT.toFixed(2), "rgba(255,255,255,0.8)");
        });

        awayXTPlayers.forEach((p, i) => {
          const y = topY + i * pitchWidth * 0.017;
          this.writeText(svg, awayPlayerStart, y, "start", playerFont, this.truncateText(p.player, pitchWidth * 0.012, playerNameW), "white");
          this.writeText(svg, awayPlayerStart + playerXTOffset, y, "end", playerFont, p.xT.toFixed(2), "rgba(255,255,255,0.8)");
        });

        const vDivTop = rectY + pitchWidth * 0.006;
        const vDivBottom = topY + 3 * pitchWidth * 0.017;
        svg.append("line")
          .attr("x1", vDivLeftX).attr("y1", vDivTop)
          .attr("x2", vDivLeftX).attr("y2", vDivBottom)
          .attr("stroke", "rgba(255,255,255,0.25)").attr("stroke-width", 1);
        svg.append("line")
          .attr("x1", vDivRightX).attr("y1", vDivTop)
          .attr("x2", vDivRightX).attr("y2", vDivBottom)
          .attr("stroke", "rgba(255,255,255,0.25)").attr("stroke-width", 1);

        const statLabelFont = (pitchWidth * 0.012) + "px";
        const statValueFont = (pitchWidth * 0.018) + "px";

        const playsLabel = Dictionary[this.language]["game"]["gameThreat"]["legendPlays"];
        const xTPerPlayLabel = 'xT / ' + Dictionary[this.language]["game"]["gameThreat"]["legendPerPlay"];

        const homeValX = centerX - pitchWidth * 0.055;
        const awayValX = centerX + pitchWidth * 0.055;

        const playsY = topY;
        this.writeText(svg, homeValX, playsY, "end", statValueFont, `${homePlayCount}`, "white");
        this.writeText(svg, centerX, playsY + pitchWidth * 0.003, "middle", statLabelFont, playsLabel, "rgba(255,255,255,0.7)");
        this.writeText(svg, awayValX, playsY, "start", statValueFont, `${awayPlayCount}`, "white");

        const xapsY = playsY + pitchWidth * 0.025;
        this.writeText(svg, homeValX, xapsY, "end", statValueFont, homeXTPerPlay.toFixed(2), "white");
        this.writeText(svg, centerX, xapsY + pitchWidth * 0.003, "middle", statLabelFont, xTPerPlayLabel, "rgba(255,255,255,0.7)");
        this.writeText(svg, awayValX, xapsY, "start", statValueFont, awayXTPerPlay.toFixed(2), "white");

        const hDivY = vDivBottom + pitchWidth * 0.004;
        svg.append("line")
          .attr("x1", rectX + colPad)
          .attr("y1", hDivY)
          .attr("x2", rectX + rectW - colPad)
          .attr("y2", hDivY)
          .attr("stroke", "rgba(255,255,255,0.25)")
          .attr("stroke-width", 1);

        const filterLabelFont = (pitchWidth * 0.01) + "px";
        const filterValueFont = (pitchWidth * 0.012) + "px";
        const filterY = hDivY + pitchWidth * 0.006;
        const allLabel = Dictionary[this.language]["general"] && Dictionary[this.language]["general"]["all"] ? Dictionary[this.language]["general"]["all"] : 'All';
        const rightColEnd = rectX + rectW - colPad;

        const timeValue = this.filterTimeText || (Dictionary[this.language]["videoEvents"] && Dictionary[this.language]["videoEvents"]["fullGame"]) || 'Full Game';

        const filters = [
          { label: "Time", value: timeValue },
          { label: Dictionary[this.language]["game"]["gameThreat"]["typeOfPlay"] || 'Type of Action', value: this.filterShotTypeText || allLabel },
          { label: Dictionary[this.language]["game"]["gameThreat"]["actionEnding"] || 'Action Ending', value: this.filterActionEndingText || allLabel },
        ];

        const filterStartX = homePlayerStart;
        const filterGap = pitchWidth * 0.012;
        const filterItemW = pitchWidth * 0.09;

        filters.forEach((f, i) => {
          const x = filterStartX + i * (filterItemW + filterGap);
          this.writeText(svg, x, filterY, "start", filterLabelFont, f.label, "rgba(255,255,255,0.6)");
          const preservePlusN = / \(\+\d+\)$/.test(f.value);
          this.writeText(svg, x, filterY + pitchWidth * 0.012, "start", filterValueFont,
            this.truncateText(f.value, pitchWidth * 0.012, filterItemW * 0.95, preservePlusN), "white");
        });

        const legendOnlyShowing = Dictionary[this.language]["game"]["gameThreat"]["legendOnlyShowing"] || 'Only showing individual actions with xT > ';
        this.writeText(svg, rightColEnd, filterY + pitchWidth * 0.006, "end", filterValueFont,
          `* ${legendOnlyShowing}${this.minXT}`, "rgba(255,255,255,0.8)");
      },
      renderBoxes(svg, svgWidth, pitchHeight, margin, shots, language = 'en') {
        let textColor = this.currentUserFieldColor === 'white' ? 'black' : 'white';
        const shotLegend = language === 'en' ? 'plays' : 'jugadas';

        const goalArea = shots.filter(s => s.x_start >= 94.2 && s.y >= 36.8 && s.y <= 63.2).length;
        const boxRight = shots.filter(s => s.x_start >= 83 && s.y >= 21.1 && s.y < 36.8).length;
        const boxCenter = shots.filter(s => s.x_start >= 83 && s.x_start < 94.2 && s.y >= 36.8 && s.y <= 63.2).length;
        const boxLeft = shots.filter(s => s.x_start >= 83 && s.y > 63.2 && s.y < 78.9).length;
        //Out Of Box
        const outBoxDeepRight = shots.filter(s => s.x_start >= 83 && s.y < 21.1).length;
        const outBoxDeepLeft = shots.filter(s => s.x_start >= 83 && s.y > 78.9).length;
        const outBoxRight = shots.filter(s => s.x_start >= 65 && s.x_start < 83 && s.y < 21.1).length;
        const outBoxCentreRight = shots.filter(s => s.x_start >= 65 && s.x_start < 83 && s.y >= 21.1 && s.y <= 35.5).length;
        const outBoxCentre = shots.filter(s => s.x_start >= 65 && s.x_start < 83 && s.y >= 35.6 && s.y <= 64.4).length;
        const outBoxCentreLeft = shots.filter(s => s.x_start >= 65 && s.x_start < 83 && s.y >= 64.5 && s.y <= 78.9).length;
        const outBoxLeft = shots.filter(s => s.x_start >= 65 && s.x_start < 83 && s.y > 78.9).length;
        //+35
        const right35 = shots.filter(s => s.x_start >= 50 && s.x_start < 65 && s.y < 21.1).length;
        const centreRight35 = shots.filter(s => s.x_start >= 50 && s.x_start < 65 && s.y >= 21.1 && s.y <= 35.5).length;
        const centre35 = shots.filter(s => s.x_start >= 50 && s.x_start < 65 && s.y >= 35.6 && s.y <= 64.4).length;
        const centreLeft35 = shots.filter(s => s.x_start >= 50 && s.x_start < 65 && s.y >= 64.5 && s.y <= 78.9).length;
        const left35 = shots.filter(s => s.x_start >= 50 && s.x_start < 65 && s.y > 78.9).length;

        var shadowScale = d3.scale.linear().domain([0, shots.length]).range([0.1, 0.75]);

        if (outBoxDeepLeft > 0.5) {
          this.drawRectangle(svg, 0.094 * svgWidth, 0.105 * pitchHeight, 0.155 * svgWidth, 0.25 * pitchHeight, "red", shadowScale(outBoxDeepLeft), "gameLegend1");
          this.writeText(svg, 0.177 * svgWidth, 0.2 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `${ Math.round(outBoxDeepLeft / shots.length * 100) } %`,textColor);
          this.writeText(svg, 0.177 * svgWidth, 0.23 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(outBoxDeepLeft) } ${ shotLegend })`,textColor);
        }
        if (outBoxLeft > 0.5) {
          this.drawRectangle(svg, 0.094 * svgWidth, 0.365 * pitchHeight, 0.155 * svgWidth, 0.295 * pitchHeight, "red", shadowScale(outBoxLeft), "gameLegend1");
          this.writeText(svg, 0.177 * svgWidth, 0.46 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `${ Math.round(outBoxLeft / shots.length * 100) } %`,textColor);
          this.writeText(svg, 0.177 * svgWidth, 0.49 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(outBoxLeft) } ${ shotLegend })`,textColor);
        }
        if (left35 > 0.5) {
          this.drawRectangle(svg, 0.094 * svgWidth, 0.665 * pitchHeight, 0.155 * svgWidth, 0.24 * pitchHeight, "red", shadowScale(left35), "gameLegend1");
          this.writeText(svg, 0.177 * svgWidth, 0.735 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `${ Math.round(left35 / shots.length * 100) } %`,textColor);
          this.writeText(svg, 0.177 * svgWidth, 0.765 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(left35) } ${ shotLegend })`,textColor);
        }
        if (outBoxDeepRight > 0.5) {
          this.drawRectangle(svg, 0.76 * svgWidth, 0.105 * pitchHeight, 0.155 * svgWidth, 0.25 * pitchHeight, "red", shadowScale(outBoxDeepRight), "gameLegend1");
          this.writeText(svg, 0.842 * svgWidth, 0.2 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `${ Math.round(outBoxDeepRight / shots.length * 100) } %`,textColor);
          this.writeText(svg, 0.842 * svgWidth, 0.23 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(outBoxDeepRight) } ${ shotLegend })`,textColor);
        }
        if (outBoxRight > 0.5) {
          this.drawRectangle(svg, 0.76 * svgWidth, 0.365 * pitchHeight, 0.155 * svgWidth, 0.295 * pitchHeight, "red", shadowScale(outBoxRight), "gameLegend1");
          this.writeText(svg, 0.842 * svgWidth, 0.46 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `${ Math.round(outBoxRight / shots.length * 100) } %`,textColor);
          this.writeText(svg, 0.842 * svgWidth, 0.49 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(outBoxRight) } ${ shotLegend })`,textColor);
        }
        if (right35 > 0.5) {
          this.drawRectangle(svg, 0.76 * svgWidth, 0.665 * pitchHeight, 0.155 * svgWidth, 0.24 * pitchHeight, "red", shadowScale(right35), "gameLegend1");
          this.writeText(svg, 0.842 * svgWidth, 0.735 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `${ Math.round(right35 / shots.length * 100) } %`,textColor);
          this.writeText(svg, 0.842 * svgWidth, 0.765 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(right35) } ${ shotLegend })`,textColor);
        }

        if (outBoxCentreLeft > 0.5) {
          this.drawRectangle(svg, 0.254 * svgWidth, 0.365 * pitchHeight, 0.13 * svgWidth, 0.295 * pitchHeight, "red", shadowScale(outBoxCentreLeft), "gameLegend1");
          this.writeText(svg, 0.33 * svgWidth, 0.46 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `${ Math.round(outBoxCentreLeft / shots.length * 100) } %`,textColor);
          this.writeText(svg, 0.33 * svgWidth, 0.49 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(outBoxCentreLeft) } ${ shotLegend })`,textColor);
        }
        if (outBoxCentre > 0.5) {
          this.drawRectangle(svg, 0.39 * svgWidth, 0.365 * pitchHeight, 0.23 * svgWidth, 0.295 * pitchHeight, "red", shadowScale(outBoxCentre), "gameLegend1");
          this.writeText(svg, 0.505 * svgWidth, 0.46 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `${ Math.round(outBoxCentre / shots.length * 100) } %`,textColor);
          this.writeText(svg, 0.505 * svgWidth, 0.49 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(outBoxCentre) } ${ shotLegend })`,textColor);
        }
        if (outBoxCentreRight > 0.5) {
          this.drawRectangle(svg, 0.625 * svgWidth, 0.365 * pitchHeight, 0.13 * svgWidth, 0.295 * pitchHeight, "red", shadowScale(outBoxCentreRight), "gameLegend1");
          this.writeText(svg, 0.69 * svgWidth, 0.46 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `${ Math.round(outBoxCentreRight / shots.length * 100) } %`,textColor);
          this.writeText(svg, 0.69 * svgWidth, 0.49 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(outBoxCentreRight) } ${ shotLegend })`,textColor);
        }

        if (centreLeft35 > 0.5) {
          this.drawRectangle(svg, 0.254 * svgWidth, 0.665 * pitchHeight, 0.13 * svgWidth, 0.24 * pitchHeight, "red", shadowScale(centreLeft35), "gameLegend1");
          this.writeText(svg, 0.33 * svgWidth, 0.69 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `${ Math.round(centreLeft35 / shots.length * 100) } %`,textColor);
          this.writeText(svg, 0.33 * svgWidth, 0.72 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(centreLeft35) } ${ shotLegend })`,textColor);
        }

        if (centre35 > 0.5) {
          this.drawRectangle(svg, 0.39 * svgWidth, 0.665 * pitchHeight, 0.23 * svgWidth, 0.24 * pitchHeight, "red", shadowScale(centre35), "gameLegend1");
          this.writeText(svg, 0.505 * svgWidth, 0.72 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `${ Math.round(centre35 / shots.length * 100) } %`,textColor);
          this.writeText(svg, 0.505 * svgWidth, 0.69 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(centre35) } ${ shotLegend })`,textColor);
        }
        if (centreRight35 > 0.5) {
          this.drawRectangle(svg, 0.625 * svgWidth, 0.665 * pitchHeight, 0.13 * svgWidth, 0.24 * pitchHeight, "red", shadowScale(centreRight35), "gameLegend1");
          this.writeText(svg, 0.69 * svgWidth, 0.69 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `${ Math.round(centreRight35 / shots.length * 100) } %`,textColor);
          this.writeText(svg, 0.69 * svgWidth, 0.72 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(centreRight35) } ${ shotLegend })`,textColor);
        }
        if (boxLeft > 0.5) {
          this.drawRectangle(svg, 0.265 * svgWidth, 0.105 * pitchHeight, 0.12 * svgWidth, 0.24 * pitchHeight, "red", shadowScale(boxLeft), "gameLegend1");
          this.writeText(svg, 0.33 * svgWidth, 0.20 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `${ Math.round(boxLeft / shots.length * 100) } %`,textColor);
          this.writeText(svg, 0.33 * svgWidth, 0.23 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(boxLeft) } ${ shotLegend })`,textColor);
        }
        if (boxRight > 0.5) {
          this.drawRectangle(svg, 0.625 * svgWidth, 0.105 * pitchHeight, 0.12 * svgWidth, 0.24 * pitchHeight, "red", shadowScale(boxRight), "gameLegend1");
          this.writeText(svg, 0.69 * svgWidth, 0.20 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `${ Math.round(boxRight / shots.length * 100) } %`,textColor);
          this.writeText(svg, 0.69 * svgWidth, 0.23 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(boxRight) } ${ shotLegend })`,textColor);
        }
        if (goalArea > 0.5) {
          this.drawRectangle(svg, 0.4 * svgWidth, 0.105 * pitchHeight, 0.21 * svgWidth, 0.068 * pitchHeight, "red", shadowScale(goalArea), "gameLegend1");
          this.writeText(svg, 0.505 * svgWidth, 0.11 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `${ Math.round(goalArea / shots.length * 100) } %`,textColor);
          this.writeText(svg, 0.505 * svgWidth, 0.14 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(goalArea) } ${ shotLegend })`,textColor);
        }
        if (boxCenter > 0.5) {
          this.drawRectangle(svg, 0.39 * svgWidth, 0.19 * pitchHeight, 0.23 * svgWidth, 0.155 * pitchHeight, "red", shadowScale(boxCenter), "gameLegend1");
          this.writeText(svg, 0.505 * svgWidth, 0.2 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `${ Math.round(boxCenter / shots.length * 100) } %`,textColor);
          this.writeText(svg, 0.505 * svgWidth, 0.23 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(boxCenter) } ${ shotLegend })`,textColor);
        }

      },
      onResize(){
        let elto =document.getElementById(this.rootElementId);
        if(elto){
          this.$emit("sizeChange", {height:elto.clientHeight,width:elto.clientWidth});
        }
      }
    },
    destroyed() {
      delete this.observer;
    },
  }

</script>



// WEBPACK FOOTER //
// src/views/base/XTChart.vue